import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslation } from "react-i18next";

interface TermsAndConditionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept?: () => void;
}

export const TermsAndConditionsDialog = ({
  open,
  onOpenChange,
  onAccept
}: TermsAndConditionsDialogProps) => {
  const { t } = useTranslation();
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col sm:max-h-[80vh]">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold">{t('terms.termsAndConditions')}</DialogTitle>
          <DialogDescription>
            Updated 17th February, 2025
          </DialogDescription>
        </DialogHeader>
        
        <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
        
        <ScrollArea className="flex-grow h-[50vh] sm:h-[60vh] pr-4 mb-2">
          <div className="space-y-4">
            <p>
              Welcome to Gebeya Inc.'s SaaS platform (the "Platform"). These Terms and Conditions ("Terms") along with the Service Level Agreement ("SLA") under Appendix A of this document, govern your access to and use of the Platform, which enables you to create and manage a marketplace connecting you with service buyers. By signing up for the Platform, you agree to be bound by these Terms. If you do not agree to these Terms, you may not use the Platform.
            </p>
            
            <h3 className="text-lg font-bold">Parties</h3>
            <p>
              Gebeya Inc., a company incorporated under the laws of the State of Delaware, ("Gebeya," "we," "us," or "our").
              <br />
              Customer, the entity or individual registering for and using the Platform ("you" or "your").
            </p>
            
            <h3 className="text-lg font-bold">Platform Overview</h3>
            <p>
              Gebeya provides a Software-as-a-Service (SaaS) platform that allows you to create and manage a marketplace connecting you with service buyers.
              <br />
              The Platform includes A service buyer app for buyers to access, book and transact with your offerings.
              <br />
              Gebeya may add new features to the Platform and will notify you of such updates.
            </p>
            
            <h3 className="text-lg font-bold">License and Subscription</h3>
            <p>
              Gebeya grants you a non-exclusive, non-transferable license to use the Platform during the Term (as defined below).
              <br />
              You are entitled to a free trial period as specified during the sign-up process. After the trial period, the applicable monthly subscription fee will apply unless you cancel your subscription before the trial ends.
              <br />
              Subscription fees are exclusive of taxes, and you are responsible for any applicable taxes.
            </p>
            
            <h3 className="text-lg font-bold">Obligations</h3>
            <p>
              <strong>Gebeya's Obligations:</strong>
              <br />
              - Perform maintenance, upgrades, and support services.
              <br />
              - Ensure the Platform operates in a professional, timely, and efficient manner.
            </p>
            <p>
              <strong>Your Obligations:</strong>
              <br />
              - Pay all fees in a timely manner to avoid service disruption.
              <br />
              - Report any technical issues to Gebeya promptly.
            </p>
            
            <h3 className="text-lg font-bold">Term and Termination</h3>
            <p>
              These Terms are effective upon your acceptance and continue for one (1) year, automatically renewing annually unless terminated by either party with 30 days' written notice.
              <br />
              During the free trial period, either party may terminate these Terms with immediate effect by providing written notice.
              <br />
              Either party may terminate these Terms immediately if the other party fails to remedy a material breach within 30 days of receiving written notice.
            </p>
            
            <h3 className="text-lg font-bold">Default</h3>
            <p>
              A material breach includes, but is not limited to:
              <br />
              - Failure to pay fees when due.
              <br />
              - Unauthorized copying, hacking, or misuse of the Platform.
              <br />
              - Insolvency or bankruptcy of either party.
              <br />
              - Failure to deliver services as outlined in these Terms.
            </p>
            
            <h3 className="text-lg font-bold">Force Majeure</h3>
            <p>
              Neither party is liable for failure to perform its obligations due to events beyond its reasonable control, such as acts of God, natural disasters, or government actions.
            </p>
            
            <h3 className="text-lg font-bold">Warranties</h3>
            <p>
              Each party warrants that:
              <br />
              - It has the authority to enter into and perform its obligations under these Terms.
              <br />
              - It is not in breach of any other agreement or law by entering into these Terms.
              <br />
              - All information provided is true, complete, and accurate.
            </p>
            
            <h3 className="text-lg font-bold">Intellectual Property</h3>
            <p>
              All intellectual property rights in the Platform remain the property of Gebeya. All rights, title and interest in and to any and all intellectual property rights relating to the Site and any Content, including copyright, patents, trademarks, trade secrets, improvements, developments, proprietary information, know-how, processes, methods, business plans or models (including computer software and preparatory and design materials thereof) and all other intellectual property (whether registered or not) developed or created from time to time, shall exclusively be owned by Gebeya. You are not authorized to and are expressly prohibited from copying, modifying, reproducing, re-publishing, sub-licensing, selling, uploading, broadcasting, posting, transmitting, transferring mirroring on any other server, or distributing any of the Content without the prior written consent of Gebeya. All other trademarks, service marks and trade names on the Site or any Content are the marks of the respective owners and any unauthorized use is prohibited. Gebeya shall be entitled, upon any actual or suspected breach of this Section by the Customer, to immediately terminate these Terms and cease to provide any service to You.
              <br />
              You retain ownership of all data generated through your use of the Platform. Upon termination, Gebeya will transfer your data to you in a secure and usable format.
            </p>
            
            <h3 className="text-lg font-bold">Data Protection</h3>
            <p>
              Both parties agree to comply with applicable data protection laws, including but not limited to the Delaware Data Protection Act and other relevant regulations.
              <br />
              The nature of the services provided by Gebeya require collation, use, storage and (sometimes) disclosure ("Use") of certain User information such as name, contact information and demographic information ("Data"). We will store your Data and use the same to contact you and otherwise use it in the normal course of providing you the service(s) to have paid/subscribed to, in accordance with the terms of our Privacy Policy. By using the Site, You consent to all actions taken by Gebeya with respect to your information in compliance with the Privacy Policy. You hereby consent to receiving communications in any form from Gebeya in connection with the Site, including but not limited to the following: by mail, by electronic mail, by telephone (landline or wireless), by website or in-app messaging, by push notification and by text messages (i.e., SMS and/or MMS communications). Furthermore, You hereby consent to receiving calls or text messages from Gebeya in connection with the Site made to wireless telephone numbers that are automatically dialed and/or include pre-recorded messages.
            </p>
            
            <h3 className="text-lg font-bold">Third Party Websites</h3>
            <p>
              The Site may contain links to other websites on the Internet that are owned and operated by third parties (including our enterprise customers), which are provided for Customer convenience only. Gebeya does not control the information, products or services available on any third party websites. The inclusion of any link does not imply our endorsement of the applicable website or any association with the website's operators. Because we have no control over such websites and resources, Customer agrees that Gebeya is not responsible or liable for the availability or the operation of such external websites, for any material located on or available from any such websites or for the protection of Customer's data privacy by third parties (other than third party service providers to Gebeya). If You decide to access any of the third party websites linked to or referred to in the Site, You do so entirely at Your own risk and subject to the terms and conditions of use for such third party websites. You further agree that Gebeya shall not be responsible or liable, directly or indirectly, for any loss or damage caused by the use of or reliance on any such material available on or through any such site.
            </p>
            
            <h3 className="text-lg font-bold">Confidentiality</h3>
            <p>
              Both parties agree to keep confidential information obtained from the other party and not disclose it to third parties, except as required by law or with prior written consent.
            </p>
            
            <h3 className="text-lg font-bold">Governing Law</h3>
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of Delaware, USA.
            </p>
            
            <h3 className="text-lg font-bold">Indemnity</h3>
            <p>
              Customer agrees and shall indemnify and hold Gebeya, its subsidiaries, affiliates, shareholders, officers, directors, agents, employees and representatives harmless from and against any claims and demands, including reasonable attorneys' fees, arising from or relating to: (a) Your use of and access to the Site; (b) any of your submissions or other Third Party Content; or (c) your violation of these Terms. This indemnification obligation will survive the termination of these Terms and your use of our services.
            </p>
            
            <h3 className="text-lg font-bold">Modification</h3>
            <p>
              Gebeya reserves the right to make modifications to the Site, the Content and any of our services at any time without notice to the Customer. Gebeya shall not be liable to you or any third party for any modification, suspension or discontinuance of any service.
            </p>
            
            <h3 className="text-lg font-bold">Miscellaneous</h3>
            <p>
              These Terms constitute the entire agreement between the parties and supersede all prior agreements.
              <br />
              No amendment to these Terms is valid unless in writing and signed by both parties.
              <br />
              If any provision of these Terms is found to be invalid, the remaining provisions remain in full force and effect.
            </p>
            
            <h2 className="text-xl font-bold mt-8">Appendix A</h2>
            <h3 className="text-lg font-semibold">General Service Level Agreement (SLA)</h3>
            <p className="text-sm italic">Version: 3.0</p>
            
            <h3 className="text-lg font-bold">1. Introduction</h3>
            <p>
              This Service Level Agreement (SLA) defines the service levels and operational commitments for the Gebeya Service Marketplace, provided by Gebeya to (the "Customer"). The Platform is a SaaS marketplace connecting you with buyers. Gebeya provides tiered SLAs for different customer groups based on their service package.
              <br />
              The SLA outlines the following:
              <br />
              Service availability and performance metrics
              <br />
              Support procedures for different customer tiers
              <br />
              Responsibilities of the provider and customer
              <br />
              Service credits and resolution times
            </p>
            
            <h3 className="text-lg font-bold">2. Definitions</h3>
            <p>
              Service Availability: The percentage of time the Service is available to the Customer, excluding scheduled maintenance and force majeure events.
              <br />
              Downtime: Periods when the Service is unavailable, excluding scheduled maintenance and force majeure events.
              <br />
              Planned Downtime: Pre-announced maintenance events communicated to the Customer.
              <br />
              Unplanned Downtime: Service interruptions due to system issues.
              <br />
              Response Time: Time taken by Gebeya to acknowledge an issue reported by the Customer.
              <br />
              Resolution Time: Time taken by Gebeya to resolve the reported issue.
              <br />
              Shared Cluster: A standard service environment used by multiple customers with shared infrastructure.
              <br />
              Premium Cluster: A higher-tier service environment with dedicated or optimized resources and enhanced service guarantees for performance and availability.
              <br />
              Service Credits: Compensation offered for service level failures.
            </p>
            
            {/* Add more SLA content here as needed - truncated for brevity */}
          </div>
        </ScrollArea>
        
        {onAccept && (
          <DialogFooter className="flex-shrink-0 pt-2 pb-1 sm:pb-2 sm:pt-3 border-t mt-auto">
            <Button 
              onClick={() => {
                onAccept();
                onOpenChange(false);
              }}
              className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 w-full sm:w-auto"
            >
              {t('terms.iAccept')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
